import { LiveChatServices as _livechat } from '@/services/store';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { defineStore } from 'pinia';
import { FormEvents } from '@/enums/form-events.enum';
import { ref } from 'vue';
import { useJeopardyStore } from '../jeopardy/jeopardy.state';
import liveChatConfig from '@/components/livechat/data/config';
import { useFormStore } from '../form/formStore';
import { useAccountStore } from '../account/account.state';
import { isIOS } from '@/utils/device-helper';
import type {
  ILiveChatUser,
  ILwiContext,
  ILiveChatSessionDataResponse,
  ILiveChatConfig,
} from '@/interfaces/entities/livechat.interface';

export const useLiveChatStore = defineStore('LiveChat', function () {
  const jpStore = useJeopardyStore();
  const formStore = useFormStore();
  const accountStore = useAccountStore();

  const liveChatEnabled = ref<boolean>();
  const lcwReady = ref<boolean>(false);
  const chatOutOfHours = ref<boolean>(false);
  const hasActiveSession = ref<boolean>(false);
  const livechatUser = ref<ILiveChatUser>({
    firstName: 'Anonymous',
    lastName: 'Customer',
    email: 'anonymous@email.com',
    membershipCard: '',
  });

  const startChat = function (config?: ILiveChatConfig): void {
    addAnalyticsForLiveChat();
    loadLiveChatScript(config);
  };

  const loadLiveChatScript = async function (config?: ILiveChatConfig): Promise<void> {
    if (!config) {
      // no config provided, use default config
      config = liveChatConfig.livechat;
    }

    return new Promise((resolve, reject) => {
      // Prevent duplicate loads
      if (document.getElementById(config.scriptId)) {
        resolve();
        return;
      }
      console.debug('[Live Chat]', 'load script');

      // Register the customization callback for chat button styling
      (window as any).lcw = function () {
        return {
          chatButtonProps: {
            controlProps: {
              hideChatTextContainer: true,
            },
            styleProps: {
              generalStyleProps: {
                minWidth: '0px',
                borderWidth: '0px',
              },
              iconStyleProps: {
                backgroundImage: 'url(https://publicstoragedigprod.blob.core.windows.net/livechat/chat-icon.svg)',
                backgroundSize: '130% 120%',
                backgroundRepeat: 'no-repeat',
                borderStyle: 'none',
                margin: '0px',
              },
            },
          },
          outOfOfficeChatButtonProps: {
            controlProps: {
              hideChatTextContainer: true,
            },
            styleProps: {
              generalStyleProps: {
                minWidth: '0px',
                borderWidth: '0px',
              },
              iconStyleProps: {
                backgroundImage: 'url(https://publicstoragedigprod.blob.core.windows.net/livechat/chat-icon.svg)',
                backgroundSize: '130% 120%',
                backgroundRepeat: 'no-repeat',
                borderStyle: 'none',
                margin: '0px',
                backgroundColor: 'rgb(0, 52, 120)',
              },
            },
          },
        };
      };

      const createScript = function (src: string, isFallback = false) {
        const script = document.createElement('script');

        script.id = config.scriptId;
        script.src = src;
        script.async = true;

        script.setAttribute('data-app-id', config.appId);
        script.setAttribute('data-widget-id', config.widgetId || config.appId);
        script.setAttribute('data-lcw-version', config.lcwVersion);
        script.setAttribute('data-org-id', config.orgId);
        script.setAttribute('data-org-url', config.orgUrl);
        script.setAttribute('data-customization-callback', 'lcw');

        script.onload = () => resolve();

        script.onerror = (error) => {
          if (!isFallback) {
            // Remove failed script and retry with fallback
            script.parentNode?.removeChild(script);
            document.body.appendChild(createScript(config.fallbackSrc, true));
          } else {
            reject(error);
          }
        };

        return script;
      };

      document.body.appendChild(createScript(config.primarySrc));
    });
  };

  const getLwiContextData = function (category: string, sessionId?: string): ILwiContext {
    // Build the pageError if jeopardy
    let pageError = '';
    if (jpStore.showJeopardy) {
      const jpErrorDescription = jpStore.jpErrorDescription?.trim();
      if (jpErrorDescription) {
        pageError = jpStore.jpCode + ': ' + jpErrorDescription;
      } else {
        pageError = jpStore.jpCode;
      }
    }

    // Retrieve the best formStep value available
    const formStep = formStore.getCurrentStepForChat();

    const lwiContexts: ILwiContext = {
      UserId: sessionId,
      FirstName: livechatUser.value?.firstName,
      LastName: livechatUser.value?.lastName,
      Email: livechatUser.value?.email,
      MembershipCard: livechatUser.value?.membershipCard,
      SourcePage: document.title,
      ReferralURL: document.referrer,
      Category: category,
      Website: typeof location !== 'undefined' ? location.toString() : '',
      Comments: jpStore.showJeopardy ? jpStore.additionalComments : '',
      FormStep: formStep,
      PageError: pageError,
      IsLoggedIn: (livechatUser.value?.isLoggedIn ?? accountStore.authenticationResult === true) ? 'true' : 'false',
    };
    return lwiContexts;
  };

  const getLiveChatSessionData = async function (
    category: string,
    sessionId?: string
  ): Promise<ILiveChatSessionDataResponse> {
    const lwiContexts = getLwiContextData(category, sessionId);
    const res = await _livechat.getLiveChatSessionData(lwiContexts);
    if (res.errors?.length) {
      throw res;
    }

    setUser(res.firstName, res.lastName, res.email, res.membershipCard, res.isLoggedIn);
    return res;
  };

  const addAnalyticsForLiveChat = function () {
    const analyticsData = {
      event: FormEvents.CHAT_STARTED,
      form: {},
    };

    useAnalyticsStore().pushAnalytics(analyticsData);
  };

  const setUser = function (
    firstName: string | null,
    lastName: string | null,
    email: string | null,
    membershipCard: string | null,
    isLoggedIn: string
  ): void {
    // For unauthenticated users, use defaults if values are null/empty
    if (!isLoggedIn) {
      livechatUser.value.firstName = firstName || 'Anonymous';
      livechatUser.value.lastName = lastName || 'Customer';
      livechatUser.value.email = email || 'anonymous@email.com';
      livechatUser.value.membershipCard = membershipCard || '';
    } else {
      livechatUser.value.firstName = firstName || '';
      livechatUser.value.lastName = lastName || '';
      livechatUser.value.email = email || '';
      livechatUser.value.membershipCard = membershipCard || '';
    }
    livechatUser.value.isLoggedIn = isLoggedIn;
  };

  const setChatEnabled = function (value: boolean) {
    if (value && jpStore.liveChatDisabled && jpStore.showJeopardy && !hasActiveSession.value) {
      // Don't enable if we shouldn't be showing the livechat
      return;
    }
    liveChatEnabled.value = value;
  };

  const setHasActiveSession = function (value: boolean) {
    hasActiveSession.value = value;
  };

  const setLcwReady = function (value: boolean) {
    lcwReady.value = value;
  };

  const setChatOutOfHours = function (value: boolean) {
    chatOutOfHours.value = value;
  };

  const checkOutOfHours = async function (config?: ILiveChatConfig): Promise<boolean> {
    const cfg = config ?? liveChatConfig.livechat;
    const url = `${cfg.orgUrl}/livechatconnector/config/${cfg.orgId}/${cfg.appId}?requestId=${crypto.randomUUID()}&channelId=lcw`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      const isOutOfHours = data?.LiveWSAndLiveChatEngJoin?.OutOfOperatingHours?.toLowerCase() === 'true';
      setChatOutOfHours(isOutOfHours);
      return isOutOfHours;
    } catch (e) {
      console.error('[Live Chat] Failed to check operating hours', e);
      return false;
    }
  };

  return {
    // state / getters
    liveChatEnabled,
    lcwReady,
    chatOutOfHours,
    hasActiveSession,
    user: livechatUser,

    // actions
    startChat,
    getLiveChatSessionData,
    addAnalyticsForLiveChat,
    setUser,
    setChatEnabled,
    setHasActiveSession,
    setLcwReady,
    setChatOutOfHours,
    checkOutOfHours,
  };
});
